import { useState, useCallback } from 'react';
import { db, SYNC_STATUS, addToSyncQueue } from '../db/shiksha-db';
import { CloudUpload } from 'lucide-react';

/**
 * The "Register" data grid — DESIGN.md §5.1.
 *
 * Features:
 * - Sticky student name column
 * - Dynamic columns based on subject
 * - "Save Pulse" micro-interaction: yellow flash → green fade on blur
 * - Writes to Dexie on blur, queues sync
 * - All inputs have sr-only labels for a11y
 * - 48px min touch targets
 */
export function RegisterGrid({ students, subject, term, marks, columns = [], onMarkSaved, batchId }) {
  const [savingCells, setSavingCells] = useState({});
  const [savedCells, setSavedCells]   = useState({});

  const handleBlur = useCallback(
    async (studentId, columnId, maxScore, value) => {
      const cellKey = `${studentId}-${subject}-${term}-${columnId}`;
      const score   = parseFloat(value);

      if (isNaN(score) || value.trim() === '') return;
      if (score < 0 || score > maxScore) {
        alert(`Score must be between 0 and ${maxScore}`);
        return;
      }

      // ── Phase 1: flash yellow (saving to IndexedDB) ──
      setSavingCells((prev) => ({ ...prev, [cellKey]: true }));

      try {
        const existing = await db.marks
          .where({ studentId, subject, term })
          .filter(m => m.componentId === columnId)
          .first();

        let recordId;
        if (existing) {
          await db.marks.update(existing.id, {
            score,
            syncStatus: SYNC_STATUS.QUEUED,
          });
          recordId = existing.id;
        } else {
          recordId = await db.marks.add({
            studentId,
            subject,
            term,
            componentId: columnId,
            score,
            maxScore: maxScore,
            syncStatus: SYNC_STATUS.QUEUED,
            batchId,
          });
        }

        await addToSyncQueue('marks', recordId, existing ? 'update' : 'create', {
          studentId,
          subject,
          term,
          componentId: columnId,
          score,
          maxScore: maxScore,
        }, batchId);

        // ── Phase 2: flash green (saved) ──
        setTimeout(() => {
          setSavingCells((prev) => ({ ...prev, [cellKey]: false }));
          setSavedCells((prev) => ({ ...prev, [cellKey]: true }));

          // Clear after animation completes
          setTimeout(() => {
            setSavedCells((prev) => ({ ...prev, [cellKey]: false }));
          }, 800);
        }, 300);

        onMarkSaved?.();
      } catch (error) {
        console.error('[RegisterGrid] Save failed:', error);
        setSavingCells((prev) => ({ ...prev, [cellKey]: false }));
      }
    },
    [subject, term, onMarkSaved],
  );

  const getCellClass = (studentId, columnId) => {
    const cellKey = `${studentId}-${subject}-${term}-${columnId}`;
    const base =
      'w-full min-h-[48px] border border-gray-300 rounded-md p-2 text-center text-base font-medium ' +
      'focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none ' +
      'transition-colors duration-300';

    if (savingCells[cellKey]) return `${base} bg-yellow-100`;
    if (savedCells[cellKey])  return `${base} animate-save-pulse`;
    return base;
  };

  const getExistingScore = (studentId, columnId) => {
    const mark = marks?.find(
      (m) => m.studentId === studentId && m.subject === subject && m.term === term && m.componentId === columnId,
    );
    return mark?.score ?? '';
  };

  const isPendingSync = (studentId) => {
    return marks?.some((m) => m.studentId === studentId && m.subject === subject && m.term === term && m.syncStatus === SYNC_STATUS.QUEUED);
  };

  // If no columns provided, render a default "Score" column (fallback)
  const renderColumns = columns.length > 0 ? columns : [{ id: 'total', label: 'Score', max: 100 }];

  return (
    <div className="overflow-x-auto border border-divider rounded-xl bg-white shadow-sm">
      <div className="min-w-max">
        {/* ── Header Row ── */}
        <div className="flex items-center border-b border-divider bg-gray-50 px-4 py-3">
          <div className="sticky left-0 bg-gray-50 z-20 w-56 flex-shrink-0">
            <span className="text-sm font-bold text-ink font-secondary">Student Name & Roll No.</span>
          </div>
          {renderColumns.map((col) => (
            <div key={col.id} className="w-32 flex-shrink-0 text-center px-2">
              <span className="text-xs font-bold text-graphite uppercase tracking-wider block truncate" title={col.label}>
                {col.label}
              </span>
              <span className="text-[10px] text-gray-500 font-medium font-secondary">Max: {col.max}</span>
            </div>
          ))}
          <div className="w-24 flex-shrink-0 text-center px-2">
            <span className="text-xs font-bold text-indigo-700 uppercase tracking-wider">Total</span>
          </div>
        </div>

        {/* ── Data Rows ── */}
        {students.map((student) => {
          // Calculate row total for UI preview
          let rowTotal = 0;
          
          return (
            <div
              key={student.id}
              className="flex items-center border-b border-divider px-4 py-2 hover:bg-indigo-50/30 transition-colors"
            >
              <div className="sticky left-0 bg-white z-10 w-56 flex-shrink-0 pr-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-indigo-700">{student.rollNo}</span>
                </div>
                <div className="truncate flex items-center gap-1">
                  <p className="text-sm font-bold text-ink truncate" title={student.name}>{student.name}</p>
                  {isPendingSync(student.id) && <CloudUpload size={14} className="text-amber-500 flex-shrink-0" title="Pending Sync" />}
                </div>
              </div>
              
              {renderColumns.map((col) => {
                const existingVal = getExistingScore(student.id, col.id);
                if (existingVal !== '') rowTotal += Number(existingVal);
                
                return (
                  <div key={col.id} className="w-32 flex-shrink-0 flex justify-center px-2">
                    <label className="sr-only" htmlFor={`mark-${student.id}-${col.id}`}>
                      {col.label} for {student.name}
                    </label>
                    <input
                      id={`mark-${student.id}-${col.id}`}
                      type="number"
                      min="0"
                      max={col.max}
                      step="0.5"
                      defaultValue={existingVal}
                      className={getCellClass(student.id, col.id)}
                      onBlur={(e) => handleBlur(student.id, col.id, col.max, e.target.value)}
                    />
                  </div>
                );
              })}
              
              <div className="w-24 flex-shrink-0 flex justify-center px-2">
                <div className="w-full min-h-[48px] bg-gray-50 border border-transparent rounded-md flex items-center justify-center">
                  <span className="text-sm font-bold text-ink">{rowTotal}</span>
                </div>
              </div>
            </div>
          );
        })}

        {students.length === 0 && (
          <div className="px-4 py-12 text-center">
            <p className="text-graphite font-medium">No students found.</p>
            <p className="text-sm text-gray-500 mt-1">Add students from the Dashboard to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
}
